import { For, Show, createSignal, onMount, onCleanup, type JSX } from "solid-js";
import "./keyboard-profile-editor.css";
import {
  BUILTIN_PROFILES,
  customProfileTemplate,
  evaluateKey,
  saveCustomProfile,
  saveProfileId,
  type KeyEventLike,
  type KeyboardProfile,
  type KeyboardRule,
  type RuleAction,
} from "./keyboard-profile";
import { codeToScancode, codeToVk } from "./keymap";

// ─────────────────────────────────────────────────────────────────────────────
// Éditeur de profil clavier — modal réutilisable.
//
// Permet à l'opérateur de :
//  - choisir un profil prédéfini (Mac / Windows) ou le profil Personnalisé ;
//  - éditer la table de règles du profil Personnalisé (ajouter / supprimer /
//    réordonner) avec un sélecteur d'action par règle ;
//  - exporter / importer le profil Personnalisé en JSON.
//
// Le profil actif est persisté via `saveProfileId` / `saveCustomProfile`.
// Les panneaux consomment le profil via `loadProfile()` (memo réactif).
//
// Composant contrôlé (comme `Drawer`) : le parent gère `open` / `onClose`.
// ─────────────────────────────────────────────────────────────────────────────

export interface KeyboardProfileEditorProps {
  open: boolean;
  onClose: () => void;
  /** Profil actuellement actif (id). */
  activeProfileId: string;
  /** Callback quand le profil actif change (le panneau met à jour son memo). */
  onProfileChange: (id: string) => void;
}

const ACTION_LABELS: Record<RuleAction["type"], string> = {
  map: "Envoyer (code naturel)",
  "swap-cmd-to-ctrl": "Cmd → Ctrl (raccourci Mac)",
  local: "Garder local",
  ignore: "Ignorer",
};

const ACTION_DESCRIPTIONS: Record<RuleAction["type"], string> = {
  map: "La touche part telle quelle au Windows distant (scancode + VK).",
  "swap-cmd-to-ctrl": "Cmd est traduit en Ctrl : Cmd+C = Ctrl+C côté Windows.",
  local: "La touche reste côté navigateur (ex. Cmd+Tab switch d'app).",
  ignore: "L'événement est jeté (ni local ni distant).",
};

const QUICK_RULE_TEMPLATES: { label: string; rule: KeyboardRule }[] = [
  {
    label: "Cmd + touche → local",
    rule: { match: { cmd: true }, action: { type: "local" } },
  },
  {
    label: "Cmd + touche → Ctrl",
    rule: { match: { cmd: true }, action: { type: "swap-cmd-to-ctrl" } },
  },
  {
    label: "Ctrl + touche → local",
    rule: { match: { ctrl: true }, action: { type: "local" } },
  },
];

function describeMatch(match: KeyboardRule["match"]): string {
  const parts: string[] = [];
  if (match.cmd) parts.push("Cmd");
  if (match.ctrl) parts.push("Ctrl");
  if (match.alt) parts.push("Alt");
  if (match.code) parts.push(match.code);
  if (match.key) parts.push(`"${match.key}"`);
  return parts.length === 0 ? "tout (joker)" : parts.join(" + ");
}

export function KeyboardProfileEditor(props: KeyboardProfileEditorProps): JSX.Element {
  // Édition locale du profil custom (copie de travail).
  const [customRules, setCustomRules] = createSignal<KeyboardRule[]>([]);
  const [importError, setImportError] = createSignal<string | null>(null);

  // Charge les règles custom existantes à l'ouverture.
  function loadCustomRules() {
    const id = props.activeProfileId;
    if (id === "custom") {
      // Recharge depuis le storage pour avoir l'état persisté.
      try {
        const raw = localStorage.getItem("rws.keyboard.custom");
        if (raw) {
          const parsed = JSON.parse(raw) as KeyboardProfile;
          if (parsed?.rules) setCustomRules(parsed.rules);
          return;
        }
      } catch {
        /* ignore */
      }
    }
    setCustomRules(customProfileTemplate().rules);
  }

  function selectProfile(id: string) {
    saveProfileId(id);
    if (id === "custom") {
      saveCustomProfile({ ...customProfileTemplate(), rules: customRules() });
    }
    props.onProfileChange(id);
  }

  function updateAction(index: number, type: RuleAction["type"]) {
    setCustomRules((prev) => prev.map((r, i) => (i === index ? { ...r, action: { type } } : r)));
  }

  function removeRule(index: number) {
    setCustomRules((prev) => prev.filter((_, i) => i !== index));
  }

  function moveRule(index: number, dir: -1 | 1) {
    setCustomRules((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function saveCustom() {
    const profile: KeyboardProfile = {
      ...customProfileTemplate(),
      rules: customRules(),
    };
    saveCustomProfile(profile);
    saveProfileId("custom");
    props.onProfileChange("custom");
  }

  function exportCustom() {
    const profile: KeyboardProfile = { ...customProfileTemplate(), rules: customRules() };
    const blob = new Blob([JSON.stringify(profile, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "remotews-keyboard-profile.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function importCustom(file: File) {
    setImportError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as KeyboardProfile;
        if (!parsed || !Array.isArray(parsed.rules)) throw new Error("JSON invalide");
        setCustomRules(parsed.rules);
      } catch (err) {
        setImportError(`Import échoué : ${String(err)}`);
      }
    };
    reader.onerror = () => setImportError("Lecture du fichier échouée");
    reader.readAsText(file);
  }

  // Escape ferme le modal.
  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape" && props.open) {
      e.preventDefault();
      props.onClose();
    }
  }
  onMount(() => document.addEventListener("keydown", handleKeyDown));
  onCleanup(() => document.removeEventListener("keydown", handleKeyDown));

  return (
    <Show when={props.open}>
      <div
        class="kpe-overlay"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        <div class="kpe-card">
          <div class="kpe-header">
            <h2 class="kpe-title">Profil clavier</h2>
            <button class="kpe-close" aria-label="Fermer" onClick={() => props.onClose()}>
              ✕
            </button>
          </div>

          <div class="kpe-body">
            {/* ── Sélecteur de profil ── */}
            <div class="kpe-section">
              <span class="kpe-section-title">Profil actif</span>
              <div class="kpe-profiles">
                <For each={BUILTIN_PROFILES}>
                  {(profile) => (
                    <button
                      class="btn btn--sm kpe-profile"
                      classList={{ "btn--info": props.activeProfileId === profile.id }}
                      onClick={() => selectProfile(profile.id)}
                      title={
                        profile.id === "mac" ? "Cmd agit comme Ctrl (raccourcis Mac)" : "Cmd = touche Windows, natif"
                      }
                    >
                      {profile.label}
                    </button>
                  )}
                </For>
                <button
                  class="btn btn--sm kpe-profile"
                  classList={{ "btn--info": props.activeProfileId === "custom" }}
                  onClick={() => {
                    loadCustomRules();
                    selectProfile("custom");
                  }}
                  title="Profil éditable par l'opérateur"
                >
                  Personnalisé
                </button>
              </div>
            </div>

            {/* ── Éditeur de règles (custom seulement) ── */}
            <Show when={props.activeProfileId === "custom"}>
              <div class="kpe-section">
                <div class="kpe-rules-header">
                  <span class="kpe-section-title">Règles (évaluées dans l'ordre)</span>
                  <div class="kpe-rules-actions">
                    <button class="btn btn--sm" onClick={() => exportCustom()} title="Exporter en JSON">
                      ↧ Exporter
                    </button>
                    <label class="btn btn--sm kpe-import-btn" title="Importer un JSON">
                      ↥ Importer
                      <input
                        type="file"
                        accept="application/json,.json"
                        class="kpe-import-input"
                        onChange={(e) => {
                          const f = e.currentTarget.files?.[0];
                          if (f) importCustom(f);
                        }}
                      />
                    </label>
                    <button class="btn btn--sm btn--info" onClick={() => saveCustom()} title="Sauvegarder et activer">
                      ✓ Sauvegarder
                    </button>
                  </div>
                </div>

                <Show when={importError()}>
                  <p class="kpe-error">{importError()}</p>
                </Show>

                <ul class="kpe-rules-list">
                  <For each={customRules()}>
                    {(rule, index) => (
                      <li class="kpe-rule">
                        <div class="kpe-rule-match">{describeMatch(rule.match)}</div>
                        <div class="kpe-rule-action">
                          <span class="kpe-rule-arrow">→</span>
                          <select
                            class="kpe-rule-select"
                            value={rule.action.type}
                            onChange={(e) => updateAction(index(), e.currentTarget.value as RuleAction["type"])}
                            title={ACTION_DESCRIPTIONS[rule.action.type]}
                          >
                            <For each={Object.keys(ACTION_LABELS) as RuleAction["type"][]}>
                              {(type) => <option value={type}>{ACTION_LABELS[type]}</option>}
                            </For>
                          </select>
                        </div>
                        <div class="kpe-rule-controls">
                          <button
                            class="kpe-icon-btn"
                            onClick={() => moveRule(index(), -1)}
                            disabled={index() === 0}
                            title="Monter"
                          >
                            ▲
                          </button>
                          <button
                            class="kpe-icon-btn"
                            onClick={() => moveRule(index(), 1)}
                            disabled={index() === customRules().length - 1}
                            title="Descendre"
                          >
                            ▼
                          </button>
                          <button
                            class="kpe-icon-btn kpe-icon-btn--danger"
                            onClick={() => removeRule(index())}
                            title="Supprimer"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    )}
                  </For>
                </ul>

                <div class="kpe-quick-add">
                  <span class="kpe-quick-add-label">Ajouter rapide :</span>
                  <For each={QUICK_RULE_TEMPLATES}>
                    {(tmpl) => (
                      <button
                        class="btn btn--sm kpe-quick-btn"
                        onClick={() => setCustomRules((prev) => [...prev, tmpl.rule])}
                      >
                        {tmpl.label}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            {/* ── Testeur de touches ── */}
            <KeyTester activeProfileId={props.activeProfileId} customRules={customRules()} />

            {/* ── Aide ── */}
            <div class="kpe-help">
              <p>
                Le profil décide ce que fait chaque combinaison de touches (Cmd+C, Ctrl+V, Cmd+Tab…). Les règles sont
                évaluées dans l'ordre : la première qui correspond gagne. La dernière règle (joker) définit le
                comportement par défaut.
              </p>
              <p>
                <strong>Cmd → Ctrl</strong> : un opérateur Mac qui tape Cmd+C envoie Ctrl+C au Windows distant.
                <strong> Local</strong> : la touche reste côté navigateur (Cmd+Tab switch d'app).
              </p>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Testeur de touches — permet de valider le mappage clavier en tapant.
//
// L'opérateur tape dans une zone ; pour chaque touche on affiche :
//  - le caractère unicode qui serait envoyé (si caractère imprimable sans modifieur) ;
//  - sinon le scancode/VK que le profil décide d'envoyer ;
//  - ou "local" / "ignoré" selon le profil.
//
// La simulation suit exactement la même logique que `onKeyDown` du panel RDP :
// toUnicodeChar d'abord, puis resolveKey. On utilise les imports partagés.
// ─────────────────────────────────────────────────────────────────────────────

interface TestEntry {
  id: number;
  key: string;
  code: string;
  modifiers: string;
  result: string;
  resultKind: "unicode" | "scancode" | "local" | "ignored";
}

function toUnicodeCharSim(e: KeyEventLike): string | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key.length !== 1) return null;
  const ch = e.key;
  if (ch < " ") return null;
  if (ch === " " || ch === "\u00a0") return null;
  return ch;
}

function KeyTester(props: { activeProfileId: string; customRules: KeyboardRule[] }): JSX.Element {
  const [entries, setEntries] = createSignal<TestEntry[]>([]);
  const [focused, setFocused] = createSignal(false);
  let counter = 0;

  function currentProfile(): KeyboardProfile {
    const id = props.activeProfileId;
    if (id === "mac") return BUILTIN_PROFILES.find((p) => p.id === "mac")!;
    if (id === "windows") return BUILTIN_PROFILES.find((p) => p.id === "windows")!;
    // custom : utilise les règles éditées en live.
    return { ...customProfileTemplate(), rules: props.customRules };
  }

  function describeModifiers(e: KeyboardEvent): string {
    const parts: string[] = [];
    if (e.ctrlKey) parts.push("Ctrl");
    if (e.metaKey) parts.push("Cmd");
    if (e.altKey) parts.push("Alt");
    if (e.shiftKey) parts.push("Shift");
    return parts.join("+");
  }

  function handleTestKey(e: KeyboardEvent): void {
    e.preventDefault();
    e.stopPropagation();

    const profile = currentProfile();
    const ev: KeyEventLike = {
      code: e.code,
      key: e.key,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
    };

    let result: string;
    let resultKind: TestEntry["resultKind"];

    // Chemin 1 : unicode (caractère imprimable sans modifieur) — comme le panel RDP.
    const unicode = toUnicodeCharSim(ev);
    if (unicode !== null) {
      result = `Envoyé : "${unicode}" (unicode)`;
      resultKind = "unicode";
    } else {
      // Chemin 2 : resolveKey (scancode / local / ignoré).
      const decision = evaluateKey(profile, ev);
      if (!decision) {
        const sc = codeToScancode(e.code);
        const vk = codeToVk(e.code);
        result = sc != null ? `Scancode 0x${sc.toString(16)} / VK 0x${(vk ?? 0).toString(16)}` : "Non mappé";
        resultKind = "scancode";
      } else if (decision.action.kind === "local") {
        result = "Gardé local (navigateur)";
        resultKind = "local";
      } else if (decision.action.kind === "ignore") {
        result = "Ignoré";
        resultKind = "ignored";
      } else {
        const action = decision.action;
        if (action.kind === "map") {
          const scStr = action.scancode != null ? `0x${action.scancode.toString(16)}` : "—";
          const vkStr = action.vk != null ? `0x${action.vk.toString(16)}` : "—";
          result = `Scancode ${scStr} / VK ${vkStr}`;
        } else {
          result = "Action inconnue";
        }
        resultKind = "scancode";
      }
    }

    const entry: TestEntry = {
      id: ++counter,
      key: e.key,
      code: e.code,
      modifiers: describeModifiers(e),
      result,
      resultKind,
    };
    setEntries((prev) => [entry, ...prev].slice(0, 15));
  }

  const resultColors: Record<TestEntry["resultKind"], string> = {
    unicode: "kpe-test-result--unicode",
    scancode: "kpe-test-result--scancode",
    local: "kpe-test-result--local",
    ignored: "kpe-test-result--ignored",
  };

  return (
    <div class="kpe-section">
      <span class="kpe-section-title">Testeur de touches</span>
      <p class="kpe-test-hint">
        Tapez ci-dessous pour voir comment chaque touche sera envoyée au Windows distant. Les caractères imprimables
        partent en unicode (respect du layout de votre clavier), les raccourcis et touches spéciales en scancode.
      </p>
      <div
        class="kpe-test-zone"
        classList={{ "kpe-test-zone--focused": focused() }}
        tabindex={0}
        onKeyDown={handleTestKey}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        {focused() ? "Tapez vos touches ici…" : "Cliquez ici puis tapez pour tester"}
      </div>
      <Show when={entries().length > 0}>
        <button class="kpe-test-clear" onClick={() => setEntries([])}>
          Vider
        </button>
        <ul class="kpe-test-list">
          <For each={entries()}>
            {(entry) => (
              <li class="kpe-test-entry">
                <div class="kpe-test-key">
                  <code>{entry.code}</code>
                  {entry.modifiers && <span class="kpe-test-mods">{entry.modifiers}</span>}
                  {entry.key.length === 1 && entry.key >= " " && <span class="kpe-test-char">"{entry.key}"</span>}
                </div>
                <div class={`kpe-test-result ${resultColors[entry.resultKind]}`}>{entry.result}</div>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}
